import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	EditPanelInputData,
	AlarisEditPanelService,
	AlarisMultiSelectDisplayWithFn,
	AlarisLanguageService,
	AlarisMultiSelect2Component,
	AlarisSelect2Component,
	AlarisIconDirective,
	AlarisOptions2Component,
	AlarisMultiOptions2Component,
	AlarisTextAreaComponent,
	AlarisLoadingDirective,
	AlarisFadeDirective, AlarisButtonComponent
} from '@campaign-portal/components-library';
import { CamaraRulesService } from '../camara-rules.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { CamaraRule, OAUTH_TYPES, RelatedEntity } from '@helpers/types/models';
import { ChangeDetectorRef } from '@angular/core';
import { CamaraRulesUpdateRequest } from "@helpers/types/hlr";
import { TranslateModule } from '@ngx-translate/core';

@Component({
	selector: 'app-edit-camara-rule',
	templateUrl: './edit-camara-rule.component.html',
	styleUrls: ['./edit-camara-rule.component.scss'],
	imports: [
		AlarisMultiSelect2Component,
		AlarisSelect2Component,
		TranslateModule,
		AlarisIconDirective,
		AlarisOptions2Component,
		AlarisMultiOptions2Component,
		AlarisTextAreaComponent,
		AlarisLoadingDirective,
		ReactiveFormsModule,
		AlarisFadeDirective,
		AlarisButtonComponent
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditCamaraRuleComponent implements OnInit {
	rule: CamaraRule;
	ruleForm = new FormGroup({
		id: new FormControl<number | null>(null),
		ruleType: new FormControl<string | null>(null, { validators: Validators.required }),
		prefixList: new FormControl<string[]>([], { validators: [Validators.required] }),
		clientList: new FormControl<number[]>([]),
		source: new FormControl<string>('', { validators: Validators.required }),
	});

	ruleTypes = OAUTH_TYPES;

	clients: RelatedEntity[] = [];
	sources: RelatedEntity[] = [];

	loading$ = new BehaviorSubject(true);
	errors = [{ key: 'required', value: 'gl.errors.required' }];

	separator = ', ';
	private readonly destroyRef = inject(DestroyRef);
	private cdr = inject(ChangeDetectorRef);

	constructor(
		@Inject(EditPanelInputData) inputData: { rule: CamaraRule },
		private rulesService: CamaraRulesService,
		private editPanel: AlarisEditPanelService,
		private readonly lService: AlarisLanguageService
	) {
		this.rule = inputData.rule || { id: null, ruleType: null, prefixList: [], clientList: [], source: '' };
		this.ruleForm.patchValue({
			id: this.rule.id,
			ruleType: this.rule.ruleType,
			prefixList: this.rule.prefixList || [],
			clientList: this.rule.clientList || [],
			source: this.rule.source || '',
		});
	}

	ngOnInit(): void {
		this.updatePrefixValidators(this.rule.ruleType);
		this.ruleForm.markAllAsTouched();

		this.ruleForm.controls.ruleType.valueChanges
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				switchMap((ruleType) => {
					this.updatePrefixValidators(ruleType);
					if (!ruleType) {
						this.clients = [];
						this.sources = [];
						this.cdr.markForCheck();
						return of(null);
					}
					this.loading$.next(true);
					return forkJoin({
						clients: this.rulesService.getRelatedClients(ruleType).pipe(
							catchError(err => {
								console.error('Clients error:', err);
								return of([]);
							})
						),
						sources: this.rulesService.getRelatedSources(ruleType).pipe(
							catchError(err => {
								console.error('Sources error:', err);
								return of([]);
							})
						)
					});
				})
			)
			.subscribe((result) => {
				if (!result) {
					this.loading$.next(false);
					return;
				}
				this.clients = result.clients
					.filter(c => c.id !== null)
					.map(c => ({ id: c.id as number, name: String(c.name ?? '') }));
				this.sources = result.sources
					.filter(s => s['id'] !== null)
					.map(s => ({ id: s['id'] as number, name: String(s['name'] ?? '') }));
				this.loading$.next(false);
				this.cdr.markForCheck();
			});

		if (this.rule.ruleType) {
			this.ruleForm.controls.ruleType.setValue(this.rule.ruleType, { emitEvent: true });
		} else {
			this.loading$.next(false);
		}
	}

	private updatePrefixValidators(ruleType: string | null): void {
		const prefixControl = this.ruleForm.controls.prefixList;
		if (ruleType === 'NUMBER_VERIFICATION_DEVICE_PHONE_NUMBER') {
			prefixControl.clearValidators();
			prefixControl.setErrors(null);
		} else {
			prefixControl.setValidators([Validators.required]);
			prefixControl.updateValueAndValidity({ emitEvent: false });
			if (!prefixControl.value || prefixControl.value.length === 0) {
				prefixControl.setErrors({ required: true });
			}
		}
		prefixControl.updateValueAndValidity({ emitEvent: true });
		prefixControl.markAsTouched({ onlySelf: true });
		prefixControl.markAsDirty({ onlySelf: true });
		this.ruleForm.updateValueAndValidity({ emitEvent: true });
		this.cdr.detectChanges();
	}

	displayRuleType = (value: string | null): string => {
		if (!value) return '';
		const found = this.ruleTypes.find(rt => rt.value === value);
		return found ? found.name : value;
	};

	displayClient: AlarisMultiSelectDisplayWithFn<number> = (clientIds: number[] | null): string[] => {
		if (clientIds && clientIds.length > 0) {
			const selected = this.clients.filter(c => clientIds.includes(c.id));
			return selected.map(c => c.name);
		}
		return [this.lService.translate('gl.all')];
	};

	save(): void {
		if (this.ruleForm.invalid) return;
		this.loading$.next(true);
		const formValue = this.ruleForm.getRawValue();
		const ruleData: CamaraRulesUpdateRequest = {
			id: formValue.id || null,
			ruleType: formValue.ruleType,
			prefixList: formValue.prefixList || [],
			clientList: formValue.clientList || [],
			source: formValue.source || '',
		};
		this.rulesService
			.update(ruleData)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				this.loading$.next(false);
				if  (resp.success || resp.createdId) {
					this.editPanel.close(true);
				}});
	}

	close(): void {
		this.editPanel.close(false);
	}
}